"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DRAWER_REFUSALS = exports.UNRECORDED_EMPLOYEE_ID = exports.startCashDrawerSchema = exports.drawerMovementSchema = exports.DRAWER_HISTORY_PAGE_SIZE = exports.closeDrawerSchema = exports.closeDrawerPreviewSchema = exports.DRAWER_MOVEMENT_KINDS = exports.CASH_SOURCES = void 0;
const zod_1 = require("zod");
/**
 * The venue cash drawer — owner, 2026-10-12 (SPEC §3; design in
 * docs/superpowers/specs/2026-10-12-cash-drawer-design.md). It replaces the
 * per-employee box balance (SPEC §12.1) as the way the venue counts its cash,
 * and the per-employee shift as the drawer's ledger.
 *
 * ONE drawer for the whole venue. Every CASH payment, by anyone, on any
 * device, is stamped with the OPEN drawer session when it is written. A close
 * counts the drawer — the count FIRST, then the expected figure and the
 * difference — keeps a float, drops the rest into the safe, and opens the next
 * session with the float kept, so the drawer is never left shut. Customer
 * balance and FIB/Qi payments never enter it.
 *
 *   expected = opening float
 *            + Σ signed stamped CASH payments (isRefund ? −amount : amount)
 *            + drawer incomes − drawer expenses
 *            − pickups − owner takes + top-ups
 *
 * Routes (the API and staff builders share these names):
 *   GET  /cash-drawer/current        — `DrawerCurrentDto`
 *   POST /cash-drawer/close/preview  — `closeDrawerPreviewSchema` → `DrawerPreviewDto`; writes nothing
 *   POST /cash-drawer/close          — `closeDrawerSchema` → `DrawerCloseResultDto`
 *   POST /cash-drawer/movements      — `drawerMovementSchema` → `DrawerMovementResultDto`
 *   GET  /cash-drawer/sessions       — history, `DrawerSessionDto` rows, newest first
 *   POST /cash-drawer/start          — `startCashDrawerSchema`; owner only, once
 *
 * MONEY: typed as whole IQD (it is counted notes), stored and returned as
 * millidinar decimal strings like every other `*Md` field.
 */
/**
 * "Paid from" on an expense, "Received into" on an income
 * (`Expense.paidFrom`, `Income.receivedInto`). DRAWER moves the open
 * session's expected cash; SAFE writes a safe slip; OUTSIDE — a bank
 * transfer, the owner's own pocket — touches neither.
 */
exports.CASH_SOURCES = ['DRAWER', 'SAFE', 'OUTSIDE'];
/**
 * Cash leaving or entering the drawer mid-session, each one a numbered safe
 * slip carrying the session's id:
 *   PICKUP     — drawer → safe (DRAWER_DROP_IN, +amount on the safe);
 *   TOP_UP     — safe → drawer, change for the till (DRAWER_TOP_UP, −amount);
 *   OWNER_TAKE — drawer → the owner's hand (DRAWER_DROP_IN +amount and
 *                OWNER_WITHDRAWAL −amount, in one step).
 */
exports.DRAWER_MOVEMENT_KINDS = ['PICKUP', 'TOP_UP', 'OWNER_TAKE'];
/** Notes are whole dinars; the cap bounds a typo, not a real drawer. */
const countedIqd = zod_1.z
    .number()
    .int('Enter a whole amount.')
    .min(0, 'Amount cannot be negative.')
    .max(1_000_000_000);
/**
 * Step one of a close: the count alone. The server answers with the expected
 * cash and the difference and records nothing — the cashier counts before
 * seeing the figure they are counting towards (owner, 2026-10-12).
 */
exports.closeDrawerPreviewSchema = zod_1.z.object({
    countedCashIqd: countedIqd,
});
/**
 * The close itself. `keptFloatIqd` stays in the drawer as the next session's
 * opening float; the rest of the count (counted − kept) goes to the safe as a
 * DRAWER_DROP_IN slip.
 *
 * Kept above counted is the SERVER's refusal (`drawer_kept_exceeds_counted`),
 * not this schema's, and so is a missing note on a difference
 * (`drawer_note_required`): the dialog places a refusal code, while a schema
 * failure would come back as a generic `validation_failed`.
 */
exports.closeDrawerSchema = zod_1.z.object({
    countedCashIqd: countedIqd,
    keptFloatIqd: countedIqd,
    note: zod_1.z.string().trim().max(500).optional(),
    /**
     * The expected cash the preview SHOWED, sent back with the close — the stale
     * guard. If a payment, refund or movement moved the figure in between, the
     * server refuses `drawer_stale` before it looks at the difference, instead
     * of recording a variance against a figure nobody counted to. Whole
     * millidinars as a decimal string; may be NEGATIVE (a refund-heavy session
     * with a small float). Required: a close always follows a preview.
     */
    expectedCashMdShown: zod_1.z
        .string()
        .regex(/^-?\d+$/, 'Expected cash must be a whole number of millidinars.'),
    /**
     * The id of the OPEN session the cashier is closing (`DrawerCurrentDto
     * .session.id`) — the other half of the stale guard. The figure alone cannot
     * tell two sessions apart: a retried or concurrent second close, after a
     * first that counted an overage and kept exactly the expected figure, would
     * match the NEXT session's float and drop notes that already left. Anything
     * but the open session's id is refused `drawer_stale`.
     */
    sessionId: zod_1.z.string().min(1, 'Say which drawer session is being closed.'),
});
/** GET /cash-drawer/sessions: closed sessions, newest first, this many to a page. */
exports.DRAWER_HISTORY_PAGE_SIZE = 50;
/**
 * A mid-session movement (owner or `safe.manage`). Always positive on the
 * wire; the server signs each slip by kind. The note is the slip's only
 * explanation, so it is required.
 */
exports.drawerMovementSchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.DRAWER_MOVEMENT_KINDS),
    amountIqd: zod_1.z
        .number()
        .int('Enter a whole amount.')
        .positive('Enter an amount above zero.')
        .max(1_000_000_000),
    note: zod_1.z.string().trim().min(1, 'Say what this movement was for.').max(300),
});
/**
 * "Start using the cash drawer" — the owner, once, one way. In ONE
 * transaction: the counted safe becomes the safe's balance through an
 * ADJUSTMENT slip (the old figure included customer-balance and FIB money),
 * the counted float opens the first drawer session, and the switch turns on.
 */
exports.startCashDrawerSchema = zod_1.z.object({
    /** Every note in the safe, counted. Capped like a safe slip, not a drawer. */
    countedSafeIqd: zod_1.z
        .number()
        .int('Enter a whole amount.')
        .min(0, 'Amount cannot be negative.')
        .max(1_000_000_000_000),
    /** The notes left in the drawer to open with. */
    countedFloatIqd: countedIqd,
    note: zod_1.z.string().trim().max(500).optional(),
});
/**
 * The row "Payments taken by employee" files payments with no employee under
 * (old rows, anything a script wrote). The API names it in English; the staff
 * app words it from its own bundles, so both sides match on this id.
 */
exports.UNRECORDED_EMPLOYEE_ID = '__unrecorded__';
/** Refusals the drawer screens discriminate on. */
exports.DRAWER_REFUSALS = [
    /** The drawer is not in use yet (`cashDrawerEnabled` is off). */
    'drawer_not_enabled',
    /** "Start using the cash drawer" a second time. */
    'drawer_already_enabled',
    /**
     * The close's `expectedCashMdShown` is not the expected figure any more —
     * cash moved after the preview. Nothing was recorded; count again.
     */
    'drawer_stale',
    /** The count differs from the expected figure and no note says why. */
    'drawer_note_required',
    /** The float to keep is more than was counted. */
    'drawer_kept_exceeds_counted',
    /** A pickup or owner take larger than the cash the drawer should hold. */
    'drawer_movement_exceeds_cash',
    /** A box reset once the drawer is in use: the boxes are history now. */
    'box_retired',
    /**
     * Resetting the Portal box, at any time: it holds customer-balance
     * spending, not notes, and a reset would add it to the safe.
     */
    'portal_box_not_resettable',
    /**
     * Deleting a bill with CASH payments while the drawer is in use, without
     * saying whether the cash was handed back (`cashReturned`).
     */
    'cash_returned_required',
    /**
     * Deleting a balance refill or payout (a REFILL bill) that holds cash while
     * the drawer is in use, whatever the answer. Voiding the slip never moves
     * the customer's balance, so the drawer could not account for the notes:
     * Customers › Balance refund pays a balance out, a refill puts cash on it.
     */
    'refill_cash_not_deletable',
];
//# sourceMappingURL=cash-drawer.js.map